/* eslint-disable react/react-in-jsx-scope */
import { useLazyQuery } from "@apollo/client";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import ButtonCheckBox from "components/Button-Checkbox";
import Dialog from "components/Dialog";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { GET_PARCELS_EXPORT } from "domains/Task/graphql/queries";
import { PapsService, ParcelSize, ParcelStatus } from "generated/graphql";
import { ExportFormModalProps, IParcelExportTemplate, ParcelExportItemsParams } from "interface/export.interface";
import moment from "moment";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { extractDataToExcel } from "utils";

const useStyles = makeStyles({
  container: {
    paddingBlock: 20,
  },
});

const ParcelsExportFormModal: FC<ExportFormModalProps> = ({ open = false, handleClose, clientId }) => {
  const { text } = useText();
  const styles = useStyles();
  const dispatchAlert = useAlertDispatch();
  const [getParcelsToExport, { data }] = useLazyQuery(GET_PARCELS_EXPORT, { fetchPolicy: "no-cache" });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [exportParams, setExportParams] = useState<ParcelExportItemsParams>({
    dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
    clientId,
    dateTo: moment().endOf("month").format("YYYY-MM-DD"),
    parcelStatus: [],
    parcelSize: [],
    service: []
  });
  const services = Object.values([PapsService.Nt, PapsService.Msg]);
  const servicesOption = [...services];  
  useEffect(() => {
    setExportParams({ ...exportParams, clientId });
  }, [clientId]);

  useEffect(() => {
    if (data?.parcelsExport?.code === 200) {
      if (!data.parcelsExport.data?.parcelsCount || !data.parcelsExport.data?.parcels?.length) {
        dispatchAlert({
          severity: "warning",
          message: text.all.empty_parcel,
        });
        setIsLoading(false);
      } else {
        extractDataToExcel(
          `Parcels_export_${moment().format("YYYY-MM-DD")}`,
          cleanData(data.parcelsExport.data?.parcels)
        );
        setExportParams({
          dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
          clientId,
          dateTo: moment().endOf("month").format("YYYY-MM-DD"),
          parcelSize: [],
          parcelStatus: [],
          service: []
        });
        dispatchAlert({
          severity: "success",
          message: text.all.export_success,
        });
        setIsLoading(false);
        handleClose();
      }
    }
  }, [data]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (["dateFrom", "dateTo"].includes(e.target.name)) {
      return setExportParams({ ...exportParams, [e.target.name]: moment(e.target.value).format("YYYY-MM-DD") });
    }
    return setExportParams({ ...exportParams, [e.target.name]: e.target.value });
  };

  const dateValidation = () => {
    if (!exportParams.dateFrom || !exportParams.dateTo || moment(exportParams.dateFrom).isAfter(exportParams.dateTo)) {
      dispatchAlert({
        severity: "warning",
        message: text.all.export_date_validation,
      });
      return false;
    }
    return true;
  };

  const confirm = () => {
    if (!dateValidation()) {
      return;
    }
    setIsLoading(true);
    getParcelsToExport({
      variables: {
        filter: {
          ...exportParams,
          parcelStatus: !exportParams.parcelStatus?.length ? null : exportParams.parcelStatus,
          parcelSize: !exportParams.parcelSize?.length ? null : exportParams.parcelSize,
          service: !exportParams.service?.length ? null : exportParams.service
        },
      },
    });
  };

  
  const setSizes = (values: string[]) => setExportParams({ ...exportParams, parcelSize: values as ParcelSize[] });

  const setStatus = (values: string[]) => setExportParams({ ...exportParams, parcelStatus: values as ParcelStatus[] });

  const setService = (values: string[]) =>setExportParams({ ...exportParams, service: values as PapsService[] });

  const cleanData = (data: IParcelExportTemplate[]) =>
    data.map((field) => ({
      [text.all.parcel_UID]: field.parcel_UID,
      [text.all.receiver_fullname]: field.receiver_fullname,
      [text.all.receiver_phone_number]: field.receiver_phone_number,
      ...(field?.address
        ? {
            [text.all.address]: field?.address?.address,
          }
        : {}),
      [text.all.creation_date]: field.creation_date,
      [text.all.delivery_date]: field.delivery_date,
      [text.all.status]: field.status,
      [text.all.size]: field.size,
      [text.all.amount_to_collect]: field.amount_to_collect,
      [text.all.attempts]: field.attemps,
      [text.all.updated_at]: field.updated_date ? moment(field.updated_date).format("YYYY-MM-DD HH:mm") : "-",
      // [text.all.first_name]: field?.firstName, @TODO: must be hidden for the moment
      // [text.all.last_name]: field?.lastName, @TODO: must be hidden for the moment
      [text.all.warehouse_name]: field?.firstName,
      [text.all.phone_number]: field?.phoneNumber,
      [text.all.service]: field?.service,
      [text.all.reference]: field?.reference,
      [text.all.deleted]: field?.isDeleted
    }));

  return (
    <Dialog
      disableConfirmButton={isLoading}
      maxWidth="md"
      open={open}
      handleClose={handleClose}
      title={text.all.export_parcel}
      handleConfirm={confirm}
    >
      {isLoading ? (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress />
        </Grid>
      ) : (
        <Grid className={styles.container} container spacing={4}>
      <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={text.all.from}
              name="dateFrom"
              variant="outlined"
              onChange={handleChange}
              value={exportParams.dateFrom}
            />
          </Grid>
           <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              name="dateTo"
              label={text.all.to_date}
              variant="outlined"
              onChange={handleChange}
              value={exportParams.dateTo}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ButtonCheckBox
              titleButton={text.all.status}
              allSelectedValues={setStatus}
              values={Object.values(ParcelStatus)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ButtonCheckBox
              titleButton={text.all.size}
              allSelectedValues={setSizes}
              values={Object.values(ParcelSize)}
              fullWidth
            />
          </Grid> 
          <Grid item xs={12}>
            <ButtonCheckBox
              titleButton="Service"
             allSelectedValues={setService}
              values={servicesOption}
              fullWidth
            />
          </Grid>
        </Grid>
      )}
    </Dialog>
  );
};

export default ParcelsExportFormModal;
